import { entity } from 'simpler-state';

import { isPerfMetricsEnabled } from '../debug/performance';

/**
 * Cumulative stats for the LZ4 decode of incoming Gateway websocket frames (see `lz4Unpickle`).
 * Fed from `BaseWebSocket.service` on every binary message while `?debug=performance` is on, and
 * rendered by the `Lz4StatsDebug` panel. Local to the tab - nothing is sent anywhere.
 *
 * Samples accumulate in a plain module object and are published to the entity at most every
 * `PUBLISH_INTERVAL_MS`: a `store.set` per websocket message would re-render the panel hundreds of
 * times a second and skew the very numbers it is measuring.
 */
export type Lz4Stats = {
  count: number;
  minMs: number | null;
  maxMs: number | null;
  avgMs: number | null;
  lastMs: number | null;
  /** Samples at or above SLOW_SAMPLE_THRESHOLD_MS */
  slowCount: number;
  /** Compressed bytes received */
  rawBytes: number;
  /** Bytes produced by the decode */
  uncompressedBytes: number;
  /** Frames where the decode threw - corrupt header, bad frame, impossible declared size */
  failureCount: number;
  lastFailure: string | null;
  updatedAt: number | null;
};

export type Lz4Sample = {
  durationMs: number;
  rawBytes: number;
  uncompressedBytes: number;
};

const PUBLISH_INTERVAL_MS = 500;
const SLOW_SAMPLE_THRESHOLD_MS = 8;
const DECIMAL_PLACES = 2;

const INITIAL_STATE: Lz4Stats = {
  count: 0,
  minMs: null,
  maxMs: null,
  avgMs: null,
  lastMs: null,
  slowCount: 0,
  rawBytes: 0,
  uncompressedBytes: 0,
  failureCount: 0,
  lastFailure: null,
  updatedAt: null,
};

const store = entity(INITIAL_STATE);

let totalMs = 0;
let pending: Lz4Stats = { ...INITIAL_STATE };
let publishTimer: ReturnType<typeof setTimeout> | null = null;

const round = (value: number): number => Number(value.toFixed(DECIMAL_PLACES));

const publish = (): void => {
  publishTimer = null;
  store.set({
    ...pending,
    minMs: pending.minMs == null ? null : round(pending.minMs),
    maxMs: pending.maxMs == null ? null : round(pending.maxMs),
    avgMs: pending.count === 0 ? null : round(totalMs / pending.count),
    lastMs: pending.lastMs == null ? null : round(pending.lastMs),
  });
};

const schedulePublish = (): void => {
  pending.updatedAt = Date.now();
  if (publishTimer) {
    return;
  }
  publishTimer = setTimeout(publish, PUBLISH_INTERVAL_MS);
};

const recordSample = (sample: Lz4Sample): void => {
  if (!isPerfMetricsEnabled()) {
    return;
  }

  const { durationMs, rawBytes, uncompressedBytes } = sample;

  pending.count += 1;
  pending.lastMs = durationMs;
  pending.minMs = pending.minMs == null ? durationMs : Math.min(pending.minMs, durationMs);
  pending.maxMs = pending.maxMs == null ? durationMs : Math.max(pending.maxMs, durationMs);
  pending.rawBytes += rawBytes;
  pending.uncompressedBytes += uncompressedBytes;
  if (durationMs >= SLOW_SAMPLE_THRESHOLD_MS) {
    pending.slowCount += 1;
  }
  totalMs += durationMs;

  schedulePublish();
};

const recordFailure = (reason: string): void => {
  if (!isPerfMetricsEnabled()) {
    return;
  }

  pending.failureCount += 1;
  pending.lastFailure = reason;
  schedulePublish();
};

const reset = (): void => {
  if (publishTimer) {
    clearTimeout(publishTimer);
    publishTimer = null;
  }
  totalMs = 0;
  pending = { ...INITIAL_STATE };
  store.set({ ...INITIAL_STATE });
};

export default {
  use: () => store.use(state => state),
  get: () => store.get(),
  recordSample,
  recordFailure,
  reset,
};
